import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { usePage } from '../../context/PageContext';
import { useFetchUsers } from '../../hooks/useFetchUsers';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { IUser } from '../../interface/user';
import { Radio, RadioGroup, Spin, Text } from '../Ant';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import NoData from '../dashboard/noData';
import { GeneralEmpty, Slash } from '../icons';
import { useConflictCheck } from '../../context/ConflictCheckContext';
import { IConflictCheckParams } from '../../interface/conflictCheck';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../hooks/useEscapeToGoBack';
import SkeletonOptionList from '../common/skeleton/skeletonOptionList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import { useQueryClient } from '@tanstack/react-query';
import { useDiscardDialog } from '../../hooks/useDiscardDialog';
import { isConflictCheckFormChanged } from './isConflictCheckFormChanged';
import CustomModal from '../informationPopup/customModal';

const SelectUsers = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { setTitle } = usePage();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { updateConflictCheckParams, conflictCheckParams } = useConflictCheck();
  const isFromConflictCheck = location.state?.isFromConflictCheck ?? false;
  const { showDiscardDialog, promptDiscardIfChanged, handleDiscardConfirm, handleDiscardCancel } =
    useDiscardDialog();
  const [searchValue, setSearchValue] = useState<string>('');
  const {
    data: userList,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchUsers({
    pageSize: 10,
    sortBy: ['firstNames:ASC'],
    search: searchValue,
  });
  const refreshUserList = () => {
    queryClient.removeQueries({ queryKey: ['users-list'] });
    return refetch();
  };
  const { ref, inView } = useInView({
    threshold: 0.5, // Trigger when 50% of the target is visible
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && userList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && userList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    if (!isFromConflictCheck) {
      handleBack();
      return;
    }
    promptDiscardIfChanged(isConflictCheckFormChanged(conflictCheckParams), () => {
      goBack(undefined, 2);
      updateConflictCheckParams(null);
    });
  };
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('common:selectUsers')}
        showCross
      />,
    );
  }, []);
  const handleSelectPerson = (user: IUser) => {
    if (isFromConflictCheck) {
      updateConflictCheckParams({
        ...conflictCheckParams,
        checkedByUserId: user?.id ?? '',
        checkedByUserName: user?.fullName ?? '',
      } as IConflictCheckParams);
    }
    goBackWithSelection();
  };

  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" data-testid="small-indicator" />;
    }
    return null;
  };
  return (
    <>
      <CollapsibleListLayout
        testId="search-users"
        contentGap="gutter"
        header={
          <SearchInputWithFilters
            searchTitle={t('common:searchUsers')}
            handleSearch={(val) => {
              setSearchValue(val);
            }}
            handleRefresh={refreshUserList}
          />
        }
      >
        {userList && userList.length > 0 && (
          <RadioGroup
            data-testid="radio-group"
            className="d-flex fd-column gap-10"
            value={conflictCheckParams?.checkedByUserId}
          >
            {userList.map((user: IUser) => (
              <Radio
                key={user.id}
                value={user.id}
                data-testid={`user-radio-${user.id}`}
                onClick={() => handleSelectPerson(user)}
              >
                <Text $fontsize="13px" $color="title" $weight={500} $lineheight="20px">
                  {user.fullName}
                </Text>
              </Radio>
            ))}
          </RadioGroup>
        )}
        {hasNextPage && <div ref={ref} className="loading-indicator" />}
        {(!userList || (Object.entries(userList).length === 0 && !isLoading)) && (
          <div className="flex-grow d-flex justify-center items-center">
            <NoData
              header={t('common:noUsersFound')}
              subHeader={t('common:tryChangingSearchCriteria')}
              icon={<GeneralEmpty />}
            />
          </div>
        )}
        {isLoading ? <SkeletonOptionList /> : renderLoader()}
      </CollapsibleListLayout>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </>
  );
};

export default SelectUsers;
